import * as React from 'react';
import { Subscription, Subject } from '@packages/common';

import { sendImpressions, convertMetadataToPayload, getImpressionDataFromProduct } from './helpers';
import {
  SubscribeViewPort,
  ConditionPayload,
  ImpressionMetadata,
  ViewPortSubscriber,
  ProductImpression,
  ViewPortSubscriberPayload,
} from './types';
import { RefinementsContext } from '../..';

interface ImpressionTrackerContextValues {
  getContainer?: () => Element;
  subscribe: SubscribeViewPort;
  condition: (data: ConditionPayload) => boolean;
}

export const ImpressionTrackerContext = React.createContext<ImpressionTrackerContextValues>({} as any);

interface ImpressionTrackerProviderProps {
  containerSelector?: string;
  disable?: boolean;
  impressionMetadata: ImpressionMetadata;
  condition?: (data: ConditionPayload) => boolean;
  manualTriggerSubject?: Subject<boolean>;
}

export const ImpressionTrackerProvider: React.FC<ImpressionTrackerProviderProps> = ({
  containerSelector,
  manualTriggerSubject,
  impressionMetadata,
  condition = (_: ConditionPayload) => true,
  children,
}) => {
  const providerContainerRef = React.createRef<HTMLDivElement>();
  const visibleContainerRef = React.useRef<Element>();
  const scrollTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const manualTriggerSubscription = React.useRef<Subscription | null>(null);
  const speed = 66;
  const subscribers: Record<number, ViewPortSubscriber> = {};
  const { refinements } = React.useContext(RefinementsContext);

  const subscribe: SubscribeViewPort = (subscriber) => {
    const nextIndex = Object.keys(subscribers).length;
    subscribers[nextIndex] = subscriber;
    return {
      unsubscribe: () => {
        delete subscribers[nextIndex];
      },
    };
  };

  const sendNextImpressions = React.useCallback(
    (products: ProductImpression[]) => {
      const payload = convertMetadataToPayload(impressionMetadata);
      sendImpressions({
        ...payload,
        products,
      });
    },
    [impressionMetadata],
  );

  const nextScrollStop = React.useCallback(() => {
    if (providerContainerRef && providerContainerRef.current) {
      const { top } = providerContainerRef.current.getBoundingClientRect();

      const viewportTop = window.pageYOffset || document.documentElement.scrollTop;

      const containerTop = top + viewportTop;
      const domSpecs = {
        containerTop,
        viewportTop,
        viewportBottom: viewportTop + window.innerHeight,
      };

      const nextImpressions = Object.values(subscribers)
        .map((subscriber: ViewPortSubscriber) => subscriber(domSpecs))
        .map((product: ViewPortSubscriberPayload) => getImpressionDataFromProduct(product, refinements?.priceSort))
        .filter((impression: ProductImpression) => impression != null);

      if (nextImpressions.length) {
        sendNextImpressions(nextImpressions);
      }
    }
  }, [providerContainerRef, refinements, sendNextImpressions, subscribers]);

  const trackWindowScroll = React.useCallback(() => {
    clearTimeout(scrollTimeout.current as any);
    scrollTimeout.current = setTimeout(() => {
      nextScrollStop();
    }, speed);
  }, [nextScrollStop]);

  React.useEffect(() => {
    window.addEventListener('scroll', trackWindowScroll, false);
    if (manualTriggerSubject) {
      manualTriggerSubscription.current = manualTriggerSubject.subscribe(() => {
        nextScrollStop();
      });
    }
    setTimeout(() => nextScrollStop(), 0);
    return () => {
      if (manualTriggerSubscription.current) {
        manualTriggerSubscription.current.unsubscribe();
      }
      window.removeEventListener('scroll', trackWindowScroll, false);
    };
  }, [manualTriggerSubject, nextScrollStop, trackWindowScroll]);

  const getContainer = () => {
    if (!visibleContainerRef.current) {
      visibleContainerRef.current = providerContainerRef.current.querySelector(containerSelector);
    }

    return visibleContainerRef.current;
  };

  return (
    <ImpressionTrackerContext.Provider
      value={{
        subscribe,
        condition,
        getContainer: containerSelector ? getContainer : undefined,
      }}
    >
      <div ref={providerContainerRef} style={{ width: '100%' }}>
        {children}
      </div>
    </ImpressionTrackerContext.Provider>
  );
};
